// src/components/optimiseur/DemanderCandidat.jsx — CHEMIN 3 : « DEMANDER AU CANDIDAT ».
//
// Un lien sans compte, qui expire, pose au candidat les questions des points
// à confirmer (et lui fait relire son CV). Il répond avec ses mots, ou dit
// « je ne l'ai pas fait » ; Leman écrit ensuite ses réponses dans le CV, et
// le recruteur est prévenu. Le candidat ne voit ni le score, ni le tarif, ni
// le nom du client.
//
//   POST /besoins/:bid/cv-validation/:cid/lien { jours, criteres? }
//        → { url, expire_le, questions:[{ critere, question }], message_candidat, message }
import React, { useState } from 'react';
import { appel, dateCourte } from './api';

const BI = ({ icon }) => <i className={`bi bi-${icon}`} aria-hidden="true" />;
const DUREES = [3, 7, 14, 30];

export default function DemanderCandidat({ besoinId, candidateId, points, aCv, onLienCree, showNotification }) {
  const liste = points || [];
  const [exclus, setExclus] = useState(() => new Set());
  const [jours, setJours] = useState(7);
  const [envoi, setEnvoi] = useState(null);
  const [busy, setBusy] = useState(false);
  const choisis = liste.filter(p => !exclus.has(p.libelle));

  const basculer = (libelle) => setExclus(s => { const n = new Set(s); if (n.has(libelle)) n.delete(libelle); else n.add(libelle); return n; });
  const copier = async (texte, quoi) => {
    try { await navigator.clipboard.writeText(texte); showNotification?.(`${quoi} copié.`, 'success'); }
    catch (e) { showNotification?.(e.message, 'error'); }
  };

  const creer = async () => {
    setBusy(true);
    try {
      const corps = { jours, ...(exclus.size ? { criteres: choisis.map(p => p.libelle) } : {}) };
      const { r, j, absente } = await appel(`/besoins/${besoinId}/cv-validation/${candidateId}/lien`, { method: 'POST', corps });
      if (absente) throw new Error('Le lien au candidat arrive côté serveur.');
      if (!r.ok || j.success === false) throw new Error(j.error || `Le lien n’a pas pu être créé (${r.status}).`);
      setEnvoi(j);
      showNotification?.(j.message || 'Lien créé : copiez-le dans votre message au candidat.', 'success');
      onLienCree?.();
    } catch (e) { showNotification?.(e.message, 'error'); }
    finally { setBusy(false); }
  };

  if (!aCv) return <p className="opt-muet">Générez d’abord le CV format entreprise de ce besoin : Leman y écrira les réponses du candidat, et c’est lui que le candidat relit.</p>;

  if (envoi) {
    const posees = Array.isArray(envoi.questions) ? envoi.questions : [];
    return (
      <div className="opt-chemin">
        <div className="opt-lien">
          <code className="opt-lien__url" title={envoi.url}>{envoi.url}</code>
          <div className="opt-lien__actions">
            <button type="button" className="tuto-bulle__lien" onClick={() => copier(envoi.url, 'Lien')}><BI icon="clipboard" /> Copier le lien</button>
            {envoi.message_candidat && <button type="button" className="tuto-bulle__lien" onClick={() => copier(envoi.message_candidat, 'Message')}><BI icon="chat-left-text" /> Copier le message</button>}
          </div>
          {envoi.expire_le && <p className="opt-note">Valable jusqu’au {dateCourte(envoi.expire_le)}. Envoyez-le par votre messagerie : ERPIA n’envoie rien lui-même.</p>}
        </div>
        {posees.length > 0 && (
          <>
            <h4 className="opt-sous-titre">Questions posées au candidat ({posees.length})</h4>
            {posees.map(q => <div key={q.critere} className="opt-question">· {q.question}</div>)}
          </>
        )}
        <p className="opt-note">Quand il aura répondu, Leman écrira ses réponses dans le CV et vous serez prévenu : le suivi est juste en dessous.</p>
      </div>
    );
  }

  return (
    <div className="opt-chemin">
      <p className="opt-note">
        Le candidat reçoit un lien, sans compte : il répond aux questions avec ses mots et relit son CV. Leman écrit ses réponses dans le CV, vous êtes prévenu.
        Il ne voit ni le score, ni le tarif, ni le nom du client.
      </p>
      {liste.length > 0 ? (
        <>
          <h4 className="opt-sous-titre">Questions qui lui seront posées</h4>
          {liste.map(p => (
            <label key={p.libelle} className="opt-coche opt-coche--question">
              <input type="checkbox" checked={!exclus.has(p.libelle)} disabled={busy} onChange={() => basculer(p.libelle)} />
              <span>{p.question}</span>
            </label>
          ))}
          {!choisis.length && <p className="opt-note">Aucune question cochée : le candidat relira seulement son CV.</p>}
        </>
      ) : <p className="opt-muet">Aucun point à confirmer : le candidat relira son CV et ce qui y a été ajouté.</p>}
      <div className="opt-confirmer__pied">
        <label className="opt-champ opt-champ--ligne">
          <span className="opt-champ__lib">Lien valable</span>
          <select className="erp-field" value={jours} onChange={e => setJours(Number(e.target.value))} disabled={busy}>
            {DUREES.map(d => <option key={d} value={d}>{d} jours</option>)}
          </select>
        </label>
        <span className="tuto-bulle__espace" />
        <button type="button" className="erp-btn erp-btn--primary erp-btn--sm" onClick={creer} disabled={busy}>
          {busy ? <i className="bi bi-arrow-repeat tq-spin" aria-hidden="true" /> : <BI icon="send" />} {busy ? 'Création…' : 'Créer le lien'}
        </button>
      </div>
    </div>
  );
}
